import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectChecklistCenterModal } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklistCenterModal';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { isMultiLine } from '@components/TaskList/TaskListItem.web';
import { useOutsideClickAndEscape } from '@hooks/useOutsideClick.web';
import theme from '@themes/theme';

type ProjectChecklistItemProps = {
  width?: number;
  onSaveProjectChecklistItem: (name: string) => void;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ProjectAddChecklistItem: React.FC<ProjectChecklistItemProps> = ({
  width = 500,
  onSaveProjectChecklistItem,
  setShowInput,
}) => {
  const MAX_TASK_NAME_LENGTH = 255;
  const MIN_INPUT_HEIGHT = 16;
  const MAX_INPUT_HEIGHT = 64;

  const inputRef = useRef<HTMLDivElement | null>(null);

  const [itemName, setItemName] = useState('');

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    let text = e.currentTarget.innerText || '';

    if (text.length > MAX_TASK_NAME_LENGTH) {
      text = text.slice(0, MAX_TASK_NAME_LENGTH);
      e.currentTarget.innerText = text;
    }
    setItemName(text);
  }, []);

  const containerRef = useOutsideClickAndEscape<HTMLDivElement>(
    true,
    () => {
      if (itemName !== '') {
        setShowDiscardModal(true);
        return;
      }
      setShowInput(false);
    },
    () => {
      setItemName('');
      setShowInput(false);
    }
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();

        if (itemName !== '') {
          onSaveProjectChecklistItem(itemName);
          setItemName('');
        }

        inputRef.current?.blur(); // blur triggers collapse
        return;
      }

      const isTextKey =
        e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;

      if (isTextKey && itemName.length >= MAX_TASK_NAME_LENGTH) {
        e.preventDefault();
      }
    },
    [itemName.length]
  );

  // ⭐ NEW — When input loses focus, close editor
  const handleBlur = () => {
    if (!itemName.trim()) {
      setShowInput(false);
    }
  };

  const { t } = useTranslation();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  return (
    <>
      {showDiscardModal && (
        <ProjectChecklistCenterModal
          contentNode={
            <Box m='l'>
              <Box flexDirection='row' marginBottom='l' alignItems='center'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='labelLarge'
                  color='textPrimary'>
                  {t('shared:discardChanges') + '?'}
                </Text>
                <Icon
                  name='X'
                  variant='m'
                  onPress={() => {
                    setShowDiscardModal(false);
                  }}
                />
              </Box>

              <Text
                marginBottom='l'
                variant='webBodySecondary'
                color='textPrimary'
                style={{ alignSelf: 'flex-start' }}>
                You have unsaved changes. Are you sure you want to discard them?
              </Text>

              <Box style={{ alignSelf: 'flex-end' }} width={125}>
                <Button
                  height={48}
                  fullWidth={true}
                  variant='primary'
                  onPress={() => {
                    setShowDiscardModal(false);
                    setItemName('');
                    setShowInput(false);
                  }}
                  accessibilityLabel={t('shared:discard')}>
                  <Text variant='webSmall' color='white'>
                    {t('shared:discard')}
                  </Text>
                </Button>
              </Box>
            </Box>
          }
          onClose={() => {
            setShowDiscardModal(false);
          }}
          visible={showDiscardModal}
        />
      )}

      <div ref={containerRef}>
        <Box
          p='m'
          minHeight={98}
          width={width}
          mb='m'
          borderWidth={1}
          borderColor='grey02'
          borderRadius='xs'
          flexDirection='row'>
          <Box>
            <Box flexDirection='row' alignItems='center'>
              <Box
                flexDirection='row'
                width={width - 68}
                borderRadius='xs'
                borderWidth={1}
                borderColor='black'
                px='m'
                py='s'>
                <div
                  ref={inputRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleInput}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur} // ⭐ CLOSE ON BLUR
                  style={{
                    width: width - 100,
                    outline: 'none',
                    cursor: 'text',
                    minHeight: MIN_INPUT_HEIGHT,
                    maxHeight: MAX_INPUT_HEIGHT,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    display: 'flex',
                    alignItems: isMultiLine(itemName) ? 'flex-start' : 'center',
                    fontSize: theme.textVariants.webBodySecondary.fontSize,
                    fontFamily: theme.textVariants.webBodySecondary.fontFamily,
                    lineHeight:
                      theme.textVariants.webBodySecondary.lineHeight + 'px',
                    color: theme.colors.textSecondary,
                  }}
                  data-placeholder='Item name...'
                />
                <Text
                  ml='s'
                  variant='bodySecondary'
                  color='grey04'
                  alignSelf='flex-end'>
                  {255 - itemName.length}
                </Text>
              </Box>

              <Box
                width={16}
                height={16}
                ml='s'
                alignItems='center'
                justifyContent='center'>
                <Icon
                  name='Close'
                  variant='s'
                  color='grey05'
                  onPress={() => {
                    setItemName('');
                    setShowInput(false);
                  }}
                />
              </Box>
            </Box>

            <Text
              ml='m'
              variant='webBodyHintSubline'
              color='grey04'
              style={{ marginTop: 10 }}>
              {t('models:projects.checklist.pressShiftEnterForNewLine')}
            </Text>
          </Box>
        </Box>
      </div>
    </>
  );
};
